<template>
  <div class="flex flex-col h-full">
    <div class="p-4 bg-n-700 rounded-2xl flex-1 overflow-y-scroll scrolling-touch">
      <div
        v-if="tab.schedulingTime || tab.deliveryOrder"
        class="flex justify-between items-center pb-4 h-9 heading-3"
      >
        <span class="text-n-10">{{ t('tabs.order-info') }}</span>
        <l-button
          v-if="tab.pickupType"
          type="text"
          size="small"
          icon="edit"
          @click="editOrderInfo = true"
        />
      </div>
      <div v-if="tab.schedulingTime">
        <h2 class="pt-2 text-sm text-n-200">
          {{ t('tabs.scheduled-time') }}
        </h2>
        <div class="text-n-10">
          {{ format(tab.schedulingTime, 'dd/MM/yyyy - HH:mm') }}
        </div>
      </div>
      <div v-if="tab.deliveryOrder" class="pb-8">
        <div v-if="tab.deliveryOrder.pickupTime">
          <div>
            <h2 class="pt-2 text-sm text-n-200">
              {{ t('tabs.pickup-time') }}
            </h2>
            <div class="text-n-10">
              {{ deliveryPickUpTime }}
            </div>
          </div>
        </div>
        <div v-if="tab.deliveryOrder" class="flex flex-row justify-between items-center">
          <div v-if="tab.deliveryOrder.shipment">
            <h2 class="pt-2 text-sm text-n-200">
              {{ t('tabs.shipment-provider') }}
            </h2>
            <div class="text-n-10">
              {{ tab.deliveryOrder.shipment.provider }}
            </div>
            <div v-if="tab.deliveryOrder.shipmentCancelled" class="text-r-300">
              {{ t('tabs.shipment-cancelled') }}
            </div>
          </div>
        </div>
        <div v-if="tab.deliveryOrder.courierName">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.courier-name') }}
          </h2>
          <div class="text-n-10">
            {{ tab.deliveryOrder.courierName }}
          </div>
        </div>
        <div v-if="tab.deliveryOrder.courierPhoneNumber">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.courier-phone-number') }}
          </h2>
          <div class="text-n-10">
            {{ tab.deliveryOrder.courierPhoneNumber }}
          </div>
        </div>
        <div v-if="tab.deliveryOrder.address" class="flex flex-row items-center gap-2">
          <div>
            <h2 class="pt-2 text-sm text-n-200">
              {{ t('tabs.address') }}
            </h2>
            <div class="text-n-10">
              {{ tab.deliveryOrder.address }}
            </div>
          </div>
          <div>
            <l-button
              v-if="shouldShowMapIcon"
              icon="3-dots"
              size="small"
              @click="dropdownOpen = true"
            />
            <l-dropdown
              v-model:open="dropdownOpen"
              :options="dropdownOptions"
              :mark-selected="false"
              @click="() => (dropdownOpen = false)"
            />
          </div>
        </div>
        <div v-if="tab.deliveryOrder.addressDetails">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.address-details') }}
          </h2>
          <div class="text-n-10">
            {{ tab.deliveryOrder.addressDetails }}
          </div>
        </div>
        <div v-if="tab.deliveryOrder.preferredPaymentMethod">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.preferred-payment-method') }}
          </h2>
          <div class="text-n-10">
            {{
              t(
                `bill.${tab.deliveryOrder.preferredPaymentMethod}`,
                tab.deliveryOrder.preferredPaymentMethod
              )
            }}
          </div>
        </div>
      </div>
      <div v-if="tab.customerInfo">
        <div class="flex justify-between items-center pb-4 h-9 heading-3">
          <span class="text-n-10">{{ t('tabs.client-info') }}</span>
        </div>
        <div v-if="tab.customerInfo.name">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.customer-name') }}
          </h2>
          <div class="text-n-10 flex justify-between items-center relative">
            {{ tab.customerInfo.name }}
            <l-tooltip v-if="tab.customerInfo.internalNote" trigger="click" placement="bottom">
              <l-icon class="cursor-pointer" name="chat" />
              <template #content>
                {{ tab.customerInfo.internalNote }}
              </template>
            </l-tooltip>
          </div>
        </div>
        <div v-if="tab.customerInfo.phoneNumber">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.customer-phone-number') }}
          </h2>
          <div class="text-n-10">
            {{ parsePhoneNumber(tab.customerInfo.phoneNumber)?.formatInternational() }}
          </div>
        </div>
        <div v-if="tab.customerInfo.phoneNumberCode">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.phone-number-code') }}
          </h2>
          <div class="text-n-10">
            {{ tab.customerInfo.phoneNumberCode }}
          </div>
        </div>
        <div v-if="tab.customerInfo.email">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.customer-email') }}
          </h2>
          <div class="text-n-10">
            {{ tab.customerInfo.email }}
          </div>
        </div>
      </div>
      <div v-else>
        <div class="flex justify-between items-center pb-4 h-9 heading-3">
          <span class="text-n-10">{{ t('tabs.client-info') }}</span>
        </div>
        <l-empty-case class="mt-12" type="empty">
          {{ t('tabs.empty-client-info') }}
        </l-empty-case>
      </div>
      <div v-if="reservationLinked">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="pt-2 text-sm text-n-200">
              {{ t('reservations.source') }}
            </h2>
            <div class="text-n-10">
              {{ reservationLinked.source || 'Restaurant' }}
            </div>
          </div>
        </div>
        <div v-if="reservationLinked.name">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.customer-name') }}
          </h2>
          <div class="text-n-10">
            {{ reservationLinked.name }}
          </div>
        </div>
        <div v-if="reservationLinked.phoneNumber">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.customer-phone-number') }}
          </h2>
          <div class="text-n-10">
            {{ parsePhoneNumber(reservationLinked.phoneNumber)?.formatInternational() }}
          </div>
        </div>
        <div v-if="reservationLinked.email">
          <h2 class="pt-2 text-sm text-n-200">
            {{ t('tabs.customer-email') }}
          </h2>
          <div class="text-n-10">
            {{ reservationLinked.email }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="tab.source === 'Glovo'" class="p-3 mt-4 text-n-0 uppercase rounded-2xl bg-y-500">
      <div class="text-center">{{ t('tabs.delivery-support') }}</div>
      <div class="flex items-center justify-center text-xl font-bold">
        <!-- eslint-disable-next-line -->
        <l-icon name="phone" class="mr-2" />+34 93 003 80 60
      </div>
    </div>
    <teleport to="#appRoot">
      <new-external-tab
        type="delivery"
        v-if="editOrderInfo === true"
        :customer="{
          ...tab.customerInfo!,
          id: tab.customerId!,
          addresses: addressesFromSelectedTab
        }"
        :tab="tab"
        @close="editOrderInfo = false"
      />
      <shipment-info
        v-if="showMap || showCancelShipment"
        :tab="tab"
        :cancel="showCancelShipment"
        @close="closeShipmentInfo"
      />
    </teleport>
    <courier-selector
      v-if="showCourierPopUp"
      :open-tabs="openTabs"
      @close="showCourierPopUp = false"
      @assign-courier="courier => setCourier(courier)"
    />
  </div>
</template>

<script setup lang="ts">
import { differenceInDays, format } from 'date-fns'
import parsePhoneNumber from 'libphonenumber-js'
import { storeToRefs } from 'pinia'
import { v4 as uuid } from 'uuid'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LDropdown, LEmptyCase, LIcon, LTooltip } from '@last/core-ui/paprika'

import { useTabs } from '@/composables/useTabs'
import CourierSelector from '@/modules/tabs/CourierSelector.vue'
import ShipmentInfo from '@/modules/tabs/ShipmentInfo.vue'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { Courier } from '@/types'

import NewExternalTab from './new-tab/NewExternalTab.vue'

const props = defineProps<{
  tabId: string
}>()

const { t } = useI18n()
const { tab } = useTabs(() => props.tabId)

const reservationsStore = useReservationsStore()
const tabsStore = useTabsStore()

const { sortedReservations } = storeToRefs(reservationsStore)
const { tabs } = storeToRefs(tabsStore)
const { updateDeliveryOrderStatus } = tabsStore

const editOrderInfo = ref(false)
const showMap = ref(false)
const showCancelShipment = ref<boolean>(false)
const showCourierPopUp = ref(false)
const dropdownOpen = ref(false)

const dropdownOptions = [
  {
    label: t('tabs.show-map'),
    value: 'showMap',
    onClick: () => {
      showMap.value = true
      dropdownOpen.value = false
    }
  },
  {
    label: t('tabs.cancel-shipment'),
    value: 'showCancelShipment',
    onClick: () => {
      showCancelShipment.value = true
      dropdownOpen.value = false
    }
  }
]

const reservationLinked = computed(() =>
  sortedReservations.value.find(reservation => reservation.tabId === tab.value.id)
)

const shouldShowMapIcon = computed(
  () => tab.value.pickupType === 'ownDelivery' && !!tab.value.deliveryOrder?.address
)

const addressesFromSelectedTab = computed(() => [
  {
    id: uuid(),
    customerId: tab.value.customerId!,
    address: tab.value.deliveryOrder!.address,
    details: tab.value.deliveryOrder!.addressDetails,
    postalCode: tab.value.deliveryOrder!.postalCode
  }
])

const openTabs = computed(() => Object.values(tabs.value).filter(tab => tab.open))

const deliveryPickUpTime = computed(() => {
  if (differenceInDays(new Date(), new Date(tab.value.deliveryOrder!.pickupTime)) > 0) {
    return format(new Date(tab.value.deliveryOrder!.pickupTime), 'dd/MM/yyyy HH:mm')
  } else {
    return format(new Date(tab.value.deliveryOrder!.pickupTime), 'HH:mm')
  }
})

function closeShipmentInfo() {
  showMap.value = false
  showCancelShipment.value = false
}

function setCourier(selectedCourier: Courier) {
  showCourierPopUp.value = false
  updateDeliveryOrderStatus({
    tabId: tab.value.id,
    newStatus: 'ON_DELIVERY',
    courier: selectedCourier
  })
}
</script>
